import { useState, useEffect } from 'react'
import { Settings2 } from 'lucide-react'
import { useStore } from '@tanstack/react-form'
import { useGastoForm } from './useGastoForm'
import type { GastoFormValues } from './gastoSchema'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getFieldError } from '@/components/clientes/wizard/useClienteForm'
import { useCuentas } from '@/hooks/useMovimientos'
import { useCategoriasGasto } from '@/hooks/useGastos'
import { usePermissions } from '@/hooks/usePermissions'
import { GestionarCategoriasGastoDialog } from '../GestionarCategoriasGastoDialog'

interface GastoFormFieldsProps {
  defaultValues?: Partial<GastoFormValues>
  onSubmit: (values: GastoFormValues) => void | Promise<void>
  onCancel: () => void
  /** Reporta si el formulario tiene cambios sin guardar, para la guarda de descarte del dialog. */
  onDirtyChange?: (dirty: boolean) => void
  isSubmitting?: boolean
  isEditing?: boolean
}

export function GastoFormFields({
  defaultValues,
  onSubmit,
  onCancel,
  onDirtyChange,
  isSubmitting,
  isEditing,
}: GastoFormFieldsProps) {
  const { form } = useGastoForm({ defaultValues, onSubmit })
  const { data: cuentas = [] } = useCuentas()
  const { data: categorias = [] } = useCategoriasGasto()
  const { puede } = usePermissions()
  const [gestionarOpen, setGestionarOpen] = useState(false)

  const isDirty = useStore(form.store, (state) => state.isDirty)
  useEffect(() => {
    onDirtyChange?.(isDirty)
    return () => onDirtyChange?.(false)
  }, [isDirty, onDirtyChange])

  const esTransferencia = useStore(form.store, (state) => state.values.es_transferencia)
  const cuentaOrigenId = useStore(form.store, (state) => state.values.cuenta_id)
  const monedaOrigen = cuentas.find((c) => c.id === cuentaOrigenId)?.moneda ?? 'ARS'

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      {!isEditing && (
        <form.Field name="es_transferencia">
          {(field) => (
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="es_transferencia">Transferencia entre cuentas</Label>
                <p className="text-xs text-muted-foreground">
                  Registra el egreso en la cuenta origen y el ingreso en la destino.
                </p>
              </div>
              <Switch
                id="es_transferencia"
                checked={field.state.value}
                onCheckedChange={(checked) => {
                  field.handleChange(checked)
                  if (!checked) form.setFieldValue('cuenta_destino_id', null)
                }}
              />
            </div>
          )}
        </form.Field>
      )}

      <div className="grid grid-cols-2 gap-4">
        <form.Field name="monto">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="monto">Monto *</Label>
              <Input
                id="monto"
                type="number"
                step="0.01"
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(Number(e.target.value))}
                onBlur={field.handleBlur}
                placeholder="0.00"
              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )}
        </form.Field>

        {esTransferencia ? (
          <div className="space-y-2">
            <Label htmlFor="moneda-transferencia">Moneda</Label>
            <Input id="moneda-transferencia" value={monedaOrigen} disabled />
            <p className="text-xs text-muted-foreground">Definida por la cuenta origen</p>
          </div>
        ) : (
          <form.Field name="moneda">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="moneda">Moneda</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v as 'ARS' | 'USD')}
                >
                  <SelectTrigger id="moneda">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ARS">ARS (Pesos)</SelectItem>
                    <SelectItem value="USD">USD (Dólares)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>
        )}
      </div>

      {!esTransferencia && (
        <form.Field name="concepto">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="concepto">Concepto *</Label>
              <Input
                id="concepto"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder="Ej: Alquiler de oficina mayo"
              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )}
        </form.Field>
      )}

      <div className="grid grid-cols-2 gap-4">
        <form.Field name="fecha">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="fecha">Fecha *</Label>
              <Input
                id="fecha"
                type="date"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )}
        </form.Field>

        {!esTransferencia && (
          <form.Field name="estado_pago">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="estado_pago">Estado de pago</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v as 'pagado' | 'pendiente')}
                >
                  <SelectTrigger id="estado_pago">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pagado">Pagado</SelectItem>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>
        )}
      </div>

      <form.Field name="cuenta_id">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor="cuenta_id">{esTransferencia ? 'Cuenta origen *' : 'Cuenta *'}</Label>
            <Select
              value={field.state.value || undefined}
              onValueChange={(v) => {
                field.handleChange(v)
                // Si el destino elegido quedó incompatible con el nuevo origen
                // (misma cuenta o moneda distinta), limpiarlo: la opción se
                // deshabilita visualmente pero el valor persistiría en el form.
                const destinoId = form.state.values.cuenta_destino_id
                if (esTransferencia && destinoId) {
                  const monedaNueva = cuentas.find((c) => c.id === v)?.moneda ?? 'ARS'
                  const monedaDestino =
                    cuentas.find((c) => c.id === destinoId)?.moneda ?? 'ARS'
                  if (destinoId === v || monedaDestino !== monedaNueva) {
                    form.setFieldValue('cuenta_destino_id', null)
                  }
                }
              }}
            >
              <SelectTrigger id="cuenta_id">
                <SelectValue placeholder="Seleccioná una cuenta" />
              </SelectTrigger>
              <SelectContent>
                {cuentas.map((cuenta) => (
                  <SelectItem key={cuenta.id} value={cuenta.id}>
                    {cuenta.nombre}
                    {esTransferencia ? ` (${cuenta.moneda ?? 'ARS'})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.state.meta.errors.length > 0 && (
              <p className="text-sm text-destructive">
                {getFieldError(field.state.meta.errors)}
              </p>
            )}
          </div>
        )}
      </form.Field>

      {esTransferencia && (
        <form.Field name="cuenta_destino_id">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="cuenta_destino_id">Cuenta destino *</Label>
              <Select
                value={field.state.value || undefined}
                onValueChange={(v) => field.handleChange(v)}
              >
                <SelectTrigger id="cuenta_destino_id">
                  <SelectValue placeholder="Seleccioná la cuenta destino" />
                </SelectTrigger>
                <SelectContent>
                  {cuentas
                    .filter((cuenta) => cuenta.id !== cuentaOrigenId)
                    .map((cuenta) => {
                      const monedaCuenta = cuenta.moneda ?? 'ARS'
                      const monedaDistinta = monedaCuenta !== monedaOrigen
                      return (
                        <SelectItem key={cuenta.id} value={cuenta.id} disabled={monedaDistinta}>
                          {cuenta.nombre} ({monedaCuenta})
                          {monedaDistinta ? ' — moneda distinta' : ''}
                        </SelectItem>
                      )
                    })}
                </SelectContent>
              </Select>
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )}
        </form.Field>
      )}

      {!esTransferencia && (
      <form.Field name="categoria_id">
        {(field) => (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="categoria_id">Categoría *</Label>
              {puede.gestionarCategoriasGasto && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground"
                  onClick={() => setGestionarOpen(true)}
                >
                  <Settings2 className="h-3.5 w-3.5 mr-1" />
                  Gestionar
                </Button>
              )}
            </div>
            {categorias.length === 0 ? (
              <button
                type="button"
                onClick={() => setGestionarOpen(true)}
                className="w-full text-sm text-muted-foreground rounded-md border border-dashed p-3 text-left hover:bg-muted/50 transition-colors"
              >
                No hay categorías de gasto. Tocá acá para crear la primera.
              </button>
            ) : (
              <Select
                value={field.state.value || undefined}
                onValueChange={(v) => field.handleChange(v)}
              >
                <SelectTrigger id="categoria_id">
                  <SelectValue placeholder="Seleccioná una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map((categoria) => (
                    <SelectItem key={categoria.id} value={categoria.id}>
                      {categoria.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {field.state.meta.errors.length > 0 && (
              <p className="text-sm text-destructive">
                {getFieldError(field.state.meta.errors)}
              </p>
            )}
          </div>
        )}
      </form.Field>
      )}

      <form.Field name="observaciones">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor="observaciones">Observaciones</Label>
            <Textarea
              id="observaciones"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder="Notas adicionales (opcional)"
              rows={2}
            />
          </div>
        )}
      </form.Field>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? 'Guardando...'
            : isEditing
              ? 'Guardar cambios'
              : esTransferencia
                ? 'Registrar transferencia'
                : 'Registrar gasto'}
        </Button>
      </div>

      <GestionarCategoriasGastoDialog open={gestionarOpen} onOpenChange={setGestionarOpen} />
    </form>
  )
}
